import styled, {css} from "styled-components";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import "./HomeDashboard.css";
import {useEffect, useMemo, useState} from "react";
import {baseDistance, FLEXCENTER, fontWeight, radius} from "../../styles/styles";
import {useAppStore} from "../../../../StateManager";
import {colorPalette} from "../../styles/colorPalette";
import type {Dashboard} from "../../../../data/models/Dashboard";
import type {WidgetDto} from "../../../../generated/api/base";
import {WidgetType, XyiconFeature} from "../../../../generated/api/base";
import type {AppState} from "../../../../data/state/AppState";
import {LoaderV5} from "../../loader/LoaderV5";
import {AppUtils} from "../../../../utils/AppUtils";
import type {LogicalSeparator} from "../../../../data/models/filter/LogicalSeparator";
import type {IFieldPointer} from "../../../../data/models/field/Field";
import type {FilterOperator} from "../../../../data/models/filter/operator/FilterOperator";
import {PortfolioList} from "./PortfolioList";
import {LinksWidget} from "./LinksWidget";
import {PieChartWidget} from "./PieChartWidget";
import {RecentViewList} from "./RecentViews";
import {createHomeDashboardLayouts, getHomeDashboardWidgetKey} from "./HomeDashboardLayout";
import {HomeDashboardGrid} from "./HomeDashboardGrid";

type WidgetDataType = "static" | "portfolios" | "recent-views";

export interface IWidgetFilter {
	operator: LogicalSeparator | "*";
	conditions: {fieldRefId: IFieldPointer; operator: FilterOperator; value: any}[];
}

export interface IWidgetTitle {
	text: string;
	color: string;
}

export interface IWidgetLink {
	displayText: string;
	url: string;
	tooltip: string;
}

interface IWidgetDataCommon {
	dataType: WidgetDataType;
}

interface IBannerWidgetData extends IWidgetDataCommon {
	data: {
		title: {
			text: string;
			color: string;
			position: number;
		};
		background: {
			filename: string;
			backgroundColor: string;
			position: number;
		};
		currentDateTimestamp: {
			format: string;
			position: number;
		};
	};
}

export interface ILinkWidgetData extends IWidgetDataCommon {
	data: IWidgetLink[];
	title: IWidgetTitle;
}

export interface IPortfolioWidgetData extends IWidgetDataCommon {
	filter: IWidgetFilter;
	title: IWidgetTitle;
	sortOrder: {
		fieldName: IFieldPointer;
		order: "ASC" | "DESC";
	};
}

export interface IRecentViewWidgetData extends IWidgetDataCommon {
	title: IWidgetTitle;
}

export interface IPieChartWidgetData extends IWidgetDataCommon {
	targetFieldRefId: IFieldPointer;
	filter: IWidgetFilter;
	title: IWidgetTitle;
}

const getDateConfig = (format: string): {locale: string; options?: Intl.DateTimeFormatOptions} => {
	switch (format) {
		case "yyyy-mm-dd":
			return {locale: "en-CA"};
		case "mm/dd/yyyy":
			return {locale: "en-US"};
		case "dd/mm/yyyy":
			return {locale: "en-GB"};
		case "yyyy/mm/dd":
			return {locale: "ja-JP"};
		case "dd/mm/yy":
			return {
				locale: "en-GB",
				options: {
					day: "2-digit",
					month: "2-digit",
					year: "2-digit",
				},
			};
		case "mm/dd/yy":
			return {
				locale: "en-US",
				options: {
					day: "2-digit",
					month: "2-digit",
					year: "2-digit",
				},
			};
		case "mmm d, yyyy":
			return {
				locale: "en-US",
				options: {
					month: "short",
					day: "numeric",
					year: "numeric",
				},
			};
		case "dddd, mmmm dd":
			return {
				locale: "en-US",
				options: {
					weekday: "long",
					month: "long",
					day: "2-digit",
				},
			};
		default:
			return {
				locale: "en-GB",
				options: {
					day: "numeric",
					month: "long",
					year: "numeric",
				},
			};
	}
};

const renderWidgetContent = (widget: WidgetDto, appState: AppState) => {
	const widgetData = JSON.parse(widget.dataSourceConfiguration);

	switch (widget.widgetType) {
		case WidgetType.Banner:
			const bannerData = widgetData as IBannerWidgetData;
			const dateConfig = getDateConfig(bannerData.data.currentDateTimestamp.format);
			return (
				<WelcomeBackStyled
					$logoUrl={
						appState.app.transport.getFullPathFromServer(
							`dashboard/${bannerData.data.background?.filename}`,
							true,
							appState.organizationIdByAuthData,
						) || ""
					}
					$backgroundColor={bannerData.data.background?.backgroundColor || ""}
				>
					<WelcomeTextWrapper>
						<WelcomeTextStyled $color={bannerData.data.title.color}>
							{bannerData.data.title.text} {appState.organization?.name}!
						</WelcomeTextStyled>
					</WelcomeTextWrapper>
					<DateWrapper $color={bannerData.data.title.color}>{new Date().toLocaleDateString(dateConfig.locale, dateConfig.options)}</DateWrapper>
				</WelcomeBackStyled>
			);
		case WidgetType.List:
			switch (widgetData.dataType) {
				case "portfolios":
					return <PortfolioList widgetData={widgetData as IPortfolioWidgetData} />;
				case "recent-views":
					return <RecentViewList widgetData={widgetData as IRecentViewWidgetData} />;
				case "static":
					const linksWidgetData = widgetData as ILinkWidgetData;
					return (
						<LinksWidget
							title={linksWidgetData.title}
							links={linksWidgetData.data}
						/>
					);
				default:
					return "There is no data to display.";
			}
		case WidgetType.PieChart:
			return <PieChartWidget configuration={widgetData as IPieChartWidgetData} />;
		case null:
			return widgetData.title.text ?? "There is no data to display.";
		default:
			return "There is no data to display.";
	}
};

const getWidgetsToRender = (dashboardMaybe: Dashboard | null): WidgetDto[] => {
	const widgets: WidgetDto[] = [];
	if (dashboardMaybe && dashboardMaybe.widgets.length > 0) {
		widgets.push(...dashboardMaybe.widgets);
	}
	return widgets;
};

export const HomeDashboard = () => {
	const appState = useAppStore((state) => state.appState);
	const [dashboard, setDashboard] = useState<Dashboard | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const widgets = useMemo(() => getWidgetsToRender(dashboard), [dashboard]);
	const layouts = useMemo(() => createHomeDashboardLayouts(widgets, dashboard?.widgetLayoutList), [dashboard, widgets]);
	const widgetElements = useMemo(
		() =>
			widgets.map((widget: WidgetDto, index: number) => (
				<TileStyled
					className="react-grid-item"
					key={getHomeDashboardWidgetKey(widget, index)}
				>
					{renderWidgetContent(widget, appState)}
				</TileStyled>
			)),
		[appState, widgets],
	);

	useEffect(() => {
		const loadData = async () => {
			setIsLoading(true);
			const dashboards = await appState.app.transport.services.feature.refreshList<Dashboard>(XyiconFeature.Dashboard);
			setIsLoading(false);
			setDashboard(dashboards[0] as Dashboard);
		};

		loadData();
	}, [appState.app.transport.services.feature]);

	useEffect(() => {
		AppUtils.disableScrolling(false); // in case of anything disables the scrolling
	}, []);

	return (
		<HomeDashboardStyled $isLoading={isLoading}>
			{isLoading ? <LoaderV5 label="Loading Dashboard..." /> : <HomeDashboardGrid layouts={layouts}>{widgetElements}</HomeDashboardGrid>}
		</HomeDashboardStyled>
	);
};

const DateWrapper = styled.div<{$color?: string}>`
	font-size: 24px;
	line-height: 32px;
	font-weight: ${fontWeight.bold};
	color: ${(props) => props.$color || "white"};
`;

const WelcomeTextWrapper = styled.div`
	display: flex;
	align-items: center;
	gap: ${baseDistance.md};
`;

const WelcomeTextStyled = styled.div<{$color?: string}>`
	font-size: 40px;
	line-height: 48px;
	font-weight: ${fontWeight.bold};
	color: ${(props) => props.$color || "white"};
`;

const WelcomeBackStyled = styled.div<{$logoUrl?: string; $backgroundColor?: string}>`
	display: flex;
	justify-content: space-between;
	align-items: center;
	flex: 1;
	padding: ${baseDistance.md};
	background-color: ${colorPalette.primary.c500Primary};
	height: 100%;
	border-radius: ${radius.xl};
	color: ${colorPalette.white};

	${WelcomeTextWrapper}, ${DateWrapper} {
		z-index: 1;
	}

	${(props) => {
		if (props.$logoUrl) {
			return css`
				background-image: url("${props.$logoUrl}");
				background-size: cover;

				&::after {
					content: "";
					position: absolute;
					inset: 0;
					background-color: ${props.$backgroundColor};
					opacity: 0.5;
					border-radius: ${radius.xl};
				}
			`;
		}
	}}

	${(props) => {
		if (props.$backgroundColor) {
			return css`
				background-color: ${props.$backgroundColor};
			`;
		}
	}}
`;

const HomeDashboardStyled = styled.div<{$isLoading?: boolean}>`
	width: 100%;
	height: 100%;
	overflow: auto;
	position: relative;

	${(props) => {
		if (props.$isLoading) {
			return css`
				${FLEXCENTER}
			`;
		}
	}}
`;

const TileStyled = styled.div`
	background-color: ${colorPalette.white};
	${FLEXCENTER};
	box-shadow: 0px 4px 20px 0px #00000033;
	border-radius: ${radius.xl};
	user-select: none;
	contain: layout paint style;

	&.resizing {
		opacity: 0.9;
	}
`;
